/**
 * Section menu for the Photo List Settings page. The page had grown too long
 * to scroll back up through (Ian, 2026-09-28), so it shows one section at a
 * time, picked here. Sections sit under two bold group headings with the
 * sections indented beneath them, so a heading never reads as a menu item
 * (Ian's change to mockup A, 2026-09-28). The menu stays pinned while a long
 * section scrolls. On a phone it is a picker above the section instead.
 */
import React from "react"

import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"

export interface SettingsMenuItem {
  id: string
  label: string
  /** A short count of things waiting on this section ("1 new"). */
  badge?: string
}

export interface SettingsMenuGroup {
  label: string
  items: SettingsMenuItem[]
}

interface Props {
  groups: SettingsMenuGroup[]
  selected: string
  onSelect: (id: string) => void
  isMobile: boolean
}

export default function PhotoListSettingsMenu({ groups, selected, onSelect, isMobile }: Props) {
  if (isMobile) {
    return (
      <div style={pickerWrapStyle}>
        <label htmlFor={PICKER_ID} style={pickerLabelStyle}>
          Section
        </label>
        <select
          id={PICKER_ID}
          value={selected}
          onChange={(e) => onSelect(e.target.value)}
          style={pickerStyle}
        >
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.badge ? `${item.label} (${item.badge})` : item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    )
  }

  return (
    <nav aria-label="Settings sections" style={menuStyle}>
      {groups.map((group) => (
        <div key={group.label} style={groupStyle}>
          <div style={groupHeadingStyle}>{group.label}</div>
          {group.items.map((item) => {
            const active = item.id === selected
            return (
              <button
                key={item.id}
                type="button"
                aria-current={active ? "true" : undefined}
                onClick={() => onSelect(item.id)}
                style={itemStyle(active)}
              >
                <span>{item.label}</span>
                {item.badge && <span style={badgeStyle}>{item.badge}</span>}
              </button>
            )
          })}
        </div>
      ))}
    </nav>
  )
}

// The page has one menu, so a fixed id ties the picker to its label.
const PICKER_ID = "photo-list-settings-section"

// --- Local styles ---

const menuStyle: React.CSSProperties = {
  position: "sticky",
  top: THEME.spacing.md,
  alignSelf: "flex-start",
  flexShrink: 0,
  width: 240,
  boxSizing: "border-box",
  padding: THEME.spacing.sm,
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.md,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
}

const groupStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

const groupHeadingStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 14,
  fontWeight: 700,
  color: THEME.colors.neutral.light.textPrimary,
}

// Indented under its heading; the selected one wears the sidebar's own
// selected look (navy at 10% on white).
const itemStyle = (active: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: THEME.spacing.sm,
  marginLeft: THEME.spacing.md,
  minHeight: 40,
  padding: `${THEME.spacing.sm} ${THEME.spacing.sm}`,
  border: "none",
  borderRadius: THEME.borderRadius.sm,
  background: active ? tint(THEME.colors.navy, 0.1) : "transparent",
  color: active ? THEME.colors.navy : THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  fontSize: 14,
  fontWeight: active ? 600 : 400,
  textAlign: "left",
  cursor: "pointer",
})

const badgeStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: `2px ${THEME.spacing.sm}`,
  borderRadius: THEME.borderRadius.pill,
  background: tint(THEME.colors.semantic.warning, 0.15),
  color: THEME.colors.neutral.light.textPrimary,
  fontSize: 12,
  fontWeight: 600,
}

const pickerWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
}

const pickerLabelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const pickerStyle: React.CSSProperties = {
  boxSizing: "border-box",
  width: "100%",
  minHeight: 44,
  padding: `${THEME.spacing.sm} ${THEME.spacing.sm}`,
  fontFamily: THEME.fonts.primary,
  fontSize: 14,
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.white,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
}
